import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Base } from "../../base";
import { useDispatch, useSelector } from "../../../store";
import { JSX } from "react/jsx-runtime";
import ColumnDef from "../../../types/columnDef";
import Attachment from "../../../types/attachment";
import KeyValue from "../../../types/keyValue";
import { getJobAttachments, resetAttachment } from "../../../slices/attachment";
import moment from "moment";
import checkRole, { UserRoles } from "../../../helpers/checkRole";
import EditAttachmentForm from "../../forms/EditAttachmentForm";
import { GridCellParams } from "@mui/x-data-grid-premium";

interface Props {
  uploadAttachments: (files: File[]) => void;
  apiDeleteAttachment: (id: number) => void;
  title: string;
  attachments: Attachment[];
  disabled?: boolean;
  handleDownload?: (id: number) => void;
  getAttachment: (id: number) => void;
  handleEditAttachment: (values: Attachment) => void;
}

const JobAttachmentView: React.FC<Props> = (props) => {
  const {
    uploadAttachments,
    apiDeleteAttachment,
    title,
    attachments,
    disabled,
    handleDownload,
    getAttachment,
    handleEditAttachment,
  } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const { job } = useSelector((store) => store.job);
  const { attachment, status } = useSelector((store) => store.attachments);
  const { attachmentTypes } = useSelector((store) => store.attachmentType);
  const { loggedInUser } = useSelector((store) => store.authentication);
  const [deleteAttachmentModel, setDeleteAttachmentModel] = useState<KeyValue>({
    id: 0,
  });
  const [highlightId, setHighlightId] = useState(0);
  const [edit, setEdit] = useState<boolean>(false);
  const { simpleUserList } = useSelector((store) => store.user);
  const xs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (job.id > 0) {
      dispatch(getJobAttachments(job?.id));
    }
  }, [dispatch, job?.id]);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Name",
      align: "left",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "note",
      headerName: "Note",
      align: "left",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "originalJob",
      headerName: "Uploaded In",
      align: "left",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      mobileSize: 12,
    },
    {
      field: "type",
      headerName: "Attachment Type",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
      type: "singleSelect",
      valueOptions: attachmentTypes.map((type) => {
        return { value: type.id, label: type.name };
      }),
      renderCell: ({ value }) => <>{value.label}</>,
    },
    {
      field: "createdBy",
      headerName: "Uploaded By",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "uploadedOn",
      headerName: "Uploaded On",
      align: "left",
      headerAlign: "center",
      flex: 0.5,
      headerClassName: "header",
      mobileSize: 12,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    let jobAttachments: {
      id: number | undefined;
      name: string | undefined;
      note: string;
      originalJob: string | undefined;
      originalJobId: number | undefined;
      type: string | { value: number | undefined; label: string | undefined };
      createdBy: string | undefined;
      uploadedOn: string | undefined;
      mobileTitle: string | undefined;
      actions: JSX.Element;
    }[] = [];
    array.forEach((attachment: Attachment) => {
      const user = simpleUserList.find((u) => u.id === attachment.createdById);
      jobAttachments.push({
        id: attachment.id,
        name:
          attachment?.name?.length! > 40
            ? `${attachment.name?.slice(0, 40)}...${
                attachment.name?.split(".")[1]
              } `
            : attachment?.name,
        note: !!attachment.note ? attachment.note : "N/A",
        originalJob: attachment.originalJobTitle ?? "N/A",
        originalJobId: attachment.originalJobId ?? undefined,
        type: xs
          ? attachment.attachmentType?.name ?? "N/A"
          : {
              value: attachment.attachmentType?.id ?? undefined,
              label: attachment.attachmentType?.name ?? "N/A",
            },
        mobileTitle: attachment.name,
        createdBy: `${user?.value}`,
        uploadedOn: moment(attachment.uploadedDate).format("MM/DD/YYYY"),
        actions: (
          <Base.TableActions
            id={attachment.id!}
            handleView={
              attachment.viewable
                ? () =>
                    window.open(attachment.url, "_blank", "noopener,noreferrer")
                : undefined
            }
            handleDownload={handleDownload}
            handleDelete={
              !job.completedOn &&
              (checkRole(loggedInUser!, UserRoles.GlobalAdmin) ||
                checkRole(loggedInUser!, UserRoles.GmdAdmin))
                ? () => {
                    setDeleteAttachmentModel({
                      id: attachment.id!,
                      value: attachment.name,
                    });
                    setHighlightId(attachment.id!);
                  }
                : undefined
            }
            handleEdit={
              checkRole(loggedInUser!, UserRoles.GlobalAdmin) ||
              checkRole(loggedInUser!, UserRoles.GmdAdmin)
                ? () => {
                    getAttachment(attachment.id!);
                    setEdit(true);
                  }
                : undefined
            }
          />
        ),
      });
    });
    return jobAttachments;
  };

  const cancelDeleteAttachment = () => {
    setHighlightId(0);
    setDeleteAttachmentModel({ id: 0, value: "" });
  };

  const attachmentDelete = () => {
    apiDeleteAttachment(deleteAttachmentModel.id!);
    setDeleteAttachmentModel({ id: 0, value: "" });
    setHighlightId(0);
  };

  const onCellClick = (params: any) => {
    if (params.field === "originalJob") {
      if (params.row.originalJobId > 0) {
        window.open(
          `/job/view/${params.row.originalJobId}`,
          "_blank",
          "noopener,noreferrer",
        );
      }
    }
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <Base.Text text={title} variant="h3" />
      </Grid>
      {!job.completedOn && (
        <Grid size={{ xs: 12 }}>
          <Base.Dropzone onDrop={uploadAttachments} disabled={disabled} />
        </Grid>
      )}
      {!!attachments.length && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            "& .header": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            "& .link": {
              cursor: "pointer",
            },
            mb: 2,
          }}
        >
          <Base.TableContainer
            columns={columns}
            rows={displayRows(attachments)}
            mobileRows={displayRows(attachments)}
            totalRows={attachments.length}
            pageSize={10}
            highlightId={highlightId}
            onCellClick={onCellClick}
            getCellClassName={(params: GridCellParams<any, any, any>) => {
              return params.field === "originalJob" &&
                params.row.originalJobId > 0
                ? "link"
                : "";
            }}
          />
        </Grid>
      )}
      <Base.Dialog
        open={deleteAttachmentModel?.id! > 0}
        title={`Are you sure you would like to delete ${deleteAttachmentModel.value}?`}
        handleCancel={cancelDeleteAttachment}
        confirmText="Delete"
        confirmButtonType="delete"
        handleConfirm={attachmentDelete}
        loading={status === "loading"}
      />
      <Base.Modal
        open={!!attachment.id && edit}
        onClose={() => {
          dispatch(resetAttachment());
          setEdit(false);
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            width: 600,
            padding: 5,
            borderRadius: "15px",
          }}
        >
          <EditAttachmentForm
            handleCancel={() => {
              dispatch(resetAttachment());
            }}
            handleSubmit={(v) => {
              handleEditAttachment(v);
              setEdit(false);
            }}
          />
        </Box>
      </Base.Modal>
    </Grid>
  );
};

export default JobAttachmentView;
